import { http } from "./client";
import { getDeviceId, getDeviceName } from "@/lib/deviceIdentity";
import type { AuditLogEntry, Session, Tenant, User } from "@/types/domain";
import type { PageMeta, PageParams } from "@/types/api";

export interface LoginPayload {
  email: string;
  password: string;
  tenant_slug?: string;
  // mfa_code is a TOTP or backup code, required whenever the account
  // already has MFA enabled (see LoginResult.mfa_setup_required for the
  // opposite case - required but not yet enrolled).
  mfa_code?: string;
}

export interface LoginResult {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  expires_at: string;
  user: User;
  tenant?: Tenant;
  // mfa_setup_required is true when access_token is a short-lived MFA-setup
  // token, not a real session - refresh_token is empty in that case. The
  // caller must complete MFA enrollment (see lib/api/mfa.ts) before signing
  // in again for real.
  mfa_setup_required: boolean;
}

export interface UpdateProfilePayload {
  first_name: string;
  last_name: string;
  phone: string;
  avatar_url: string;
  email: string;
  locale: string;
}

/**
 * How much of the inactivity timeout a browser session has left. `enforced` is
 * false when the rule does not cover this session (it is off, or the session is
 * not a browser one) - the app then runs no idle timer at all.
 */
/** What happened to one session in a revoke request. `not_found` means it was
 * already gone (expired, revoked, or never yours), which is the outcome the
 * caller wanted. */
export type SessionRevokeStatus = "revoked" | "not_found" | "current_session" | "failed";
export interface SessionRevokeResult {
  id: string;
  status: SessionRevokeStatus;
}

export interface SessionActivity {
  enforced: boolean;
  last_activity_at?: string;
  idle_deadline?: string;
  idle_timeout_seconds?: number;
  warning_seconds?: number;
  remaining_seconds?: number;
}

export const authApi = {
  // client_type: "web" is attached here, not per call site, so every login
  // form gets it automatically - it's audit-only labeling (see backend
  // security.NormalizeClientType), never a basis for revoking a session.
  // device_id/device_name identify this browser for new-device approval
  // (see lib/deviceIdentity.ts) - sent on every login, used only by a
  // workspace with that feature on.
  login: (payload: LoginPayload) =>
    http.post<LoginResult>("/auth/login", {
      ...payload,
      client_type: "web",
      device_id: getDeviceId(),
      device_name: getDeviceName(),
    }),
  me: () => http.get<User>("/auth/me"),
  updateProfile: (payload: UpdateProfilePayload) => http.put<User>("/auth/profile", payload),
  // Multipart, not JSON - the "avatar" field name matches the backend
  // handler's c.FormFile("avatar"). axios auto-detects FormData and sets
  // the multipart/form-data Content-Type (with boundary) itself; the api
  // instance never hardcodes a Content-Type that would fight that.
  uploadAvatar: (file: File) => {
    const form = new FormData();
    form.append("avatar", file);
    return http.post<User>("/auth/profile/avatar", form);
  },
  removeAvatar: () => http.delete<User>("/auth/profile/avatar"),
  // Whether the company lets this person change their own picture (Admin/HR
  // always can), so the button is hidden instead of failing on upload.
  photoPolicy: () => http.get<{ can_change: boolean }>("/auth/profile/photo-policy"),
  logout: () => http.post<{ message: string }>("/auth/logout"),
  // Inactivity timeout. sessionStatus only reads; recordActivity is the
  // heartbeat (and what "Continue Session" calls) that restarts the idle clock.
  sessionStatus: () => http.get<SessionActivity>("/auth/session"),
  recordActivity: () => http.post<SessionActivity>("/auth/activity"),
  logoutEverywhere: () => http.post<{ message: string }>("/auth/logout-all"),
  sessions: () => http.get<Session[]>("/auth/sessions"),
  // Signs one other device/session out remotely - the session-scoped
  // counterpart to logoutEverywhere. Never used on the caller's own current
  // session id; SessionsCard only renders this action for non-current rows.
  revokeSession: (sessionId: string) => http.delete<SessionRevokeResult>(`/auth/sessions/${sessionId}`),
  // Several chosen devices at once. Each id gets its own outcome, so a partial
  // success is reported exactly rather than as one pass/fail.
  revokeSelectedSessions: (sessionIds: string[]) =>
    http.post<{ results: SessionRevokeResult[]; revoked: number }>("/auth/sessions/revoke-selected", { session_ids: sessionIds }),
  // Every device except the one making the request.
  revokeOtherSessions: () => http.post<{ revoked: number }>("/auth/sessions/revoke-others"),
  // Nested {items, page} shape, same reason as auditApi.list - see lib/api/audit.ts.
  loginActivity: (filters: PageParams = {}) =>
    http.get<{ items: AuditLogEntry[]; page: PageMeta }>("/auth/login-activity", filters),
  changePassword: (current_password: string, new_password: string) =>
    http.post<{ message: string }>("/auth/change-password", { current_password, new_password }),
  // Email-OTP "forgot password" flow - see ForgotPasswordPage. The response
  // shape is identical whether or not email is registered, by design.
  forgotPassword: (email: string) => http.post<{ message: string }>("/auth/forgot-password", { email }),
  verifyResetOtp: (email: string, code: string) =>
    http.post<{ reset_ticket: string }>("/auth/verify-reset-otp", { email, code }),
  resetPasswordWithOtp: (reset_ticket: string, new_password: string, confirm_password: string) =>
    http.post<{ message: string }>("/auth/reset-password", { reset_ticket, new_password, confirm_password }),
  // Whether this tenant has the password_reset feature toggle on - reaching
  // a 200 means it does; a FEATURE_DISABLED error (see ApiRequestError)
  // means a Super Admin turned it off in Feature Toggle Management.
  passwordResetAvailability: () => http.get<{ available: boolean }>("/auth/password-reset/availability"),
};
